// lib/routes.ts

import { ResourceType } from "@/app/generated/prisma"
import { ResourceTypeUnion } from "@/types/resources"

const formatTypeForUrl = (type: string): string => {
   return type?.replace(/_/g, "-")
}

export const parseResourceTypeFromUrl = (type: string): string => {
   return type?.replace(/-/g, "_")
}

// Client-side routes
const clientRoutes = {
   home: {
      path: "/",
      getPath: () => "/",
   },
   logout: {
      path: "/logout",
      getPath: () => "/logout/",
   },
   contact: {
      path: "/contact",
      getPath: () => "/contact/",
   },
   contactSuccess: {
      path: "/contact/success",
      getPath: () => "/contact/success/",
   },
   search: {
      path: "/search",
      getPath: (params: {
         query?: string
         articles?: boolean
         videos?: boolean
         library?: boolean
         templates?: boolean
      }) =>
         `/search/?${params.query ? `q=${encodeURIComponent(params.query)}` : ""}${params.articles ? `&articles=${params.articles ? 1 : 0}` : ""}${params.videos ? `&videos=${params.videos ? 1 : 0}` : ""}${params.library ? `&library=${params.library ? 1 : 0}` : ""}${params.templates ? `&templates=${params.templates ? 1 : 0}` : ""}`,
   },

   legal: {
      path: "/legal",
      getPath: () => "/legal/",
   },
   membership: {
      path: "/membership",
      getPath: () => "/membership/",
   },
   privacyPolicy: {
      path: "/legal/privacy-policy",
      getPath: () => "/legal/privacy-policy/",
   },
   termsAndConditions: {
      path: "/legal/terms-and-conditions",
      getPath: () => "/legal/terms-and-conditions/",
   },
   editorialStandards: {
      path: "/legal/editorial-standards",
      getPath: () => "/legal/editorial-standards/",
   },
   codeOfEthics: {
      path: "/legal/code-of-ethics",
      getPath: () => "/legal/code-of-ethics/",
   },
   membershipTerms: {
      path: "/legal/membership-terms",
      getPath: () => "/legal/membership-terms/",
   },
   ethics: {
      path: "/ethics",
      getPath: () => "/ethics/",
   },
   about: {
      path: "/about",
      getPath: () => "/about/",
   },
   reviewGuidelines: {
      path: "/review-guidelines",
      getPath: () => "/review-guidelines/",
   },
   disclosurePolicy: {
      path: "/disclosure-policy",
      getPath: () => "/disclosure-policy/",
   },
   resources: {
      path: "/resources",
      getPath: () => "/resources/",
   },
   posts: {
      path: "/posts",
      getPath: () => "/posts/",
   },
   post: {
      path: "/posts/[slug]",
      getPath: (params: { slug?: string }) => `/posts/${encodeURIComponent(params.slug ?? "")}/`,
   },
   redirectCallback: {
      path: "/unified/callback",
      getPath: (params: { from: string; redirect?: string }) =>
         `/unified/callback?from=${encodeURIComponent(params.from)}&redirect=${encodeURIComponent(params.redirect ?? "")}/`,
   },
   shows: {
      path: "/shows",
      getPath: () => "/shows/",
   },
   showChannel: {
      path: "/shows/[slug]",
      getPath: (params: { slug: string }) => `/shows/${encodeURIComponent(params.slug)}/`,
   },
   showEpisode: {
      path: "/shows/episodes/[videoSlug]",
      getPath: (params: { videoSlug: string; channelSlug: string }) =>
         `/shows/${encodeURIComponent(params.channelSlug ?? "")}/${encodeURIComponent(params.videoSlug ?? "")}/`,
   },

   showsQa: {
      path: "/shows-qa",
      getPath: () => "/shows-qa/",
   },
   showQaVideos: {
      path: "/shows/videos",
      getPath: () => "/shows/videos/",
   },
   showsQaChannel: {
      path: "/shows-qa/channels/[slug]",
      getPath: (params: { slug: string }) => `/shows-qa/channels/${encodeURIComponent(params.slug)}/`,
   },
   showQaEpisodes: {
      path: "/shows-qa/episodes/",
      getPath: () => "/shows-qa/episodes/",
   },
   showsQaEpisode: {
      path: "/shows-qa/[videoSlug]",
      getPath: (params: { videoSlug: string }) => `/shows-qa/${encodeURIComponent(params.videoSlug)}/`,
   },
   showQaChannelExlusive: {
      path: "/shows-qa/channels/exclusive",
      getPath: () => "/shows-qa/channels/exclusive/",
   },
   staff: {
      path: "/staff",
      getPath: () => "/staff/",
   },
   staffProfile: {
      path: "/staff/[slug]",
      getPath: (params: { slug: string }) => `/staff/${encodeURIComponent(params.slug)}/`,
   },
   resourceDownloadPage: {
      path: "/resources/[resourceType]/[categorySlug]/[resourceSlug]",
      getPath: ({ resourceType, resourceSlug }: { resourceType: ResourceTypeUnion; resourceSlug: string }) => {
         const formattedType = formatTypeForUrl(resourceType)

         switch (formattedType) {
            case "tools":
               return `/resources/${formattedType}/${encodeURIComponent(resourceSlug)}/`

            default:
               return `/resources/${formattedType}/${encodeURIComponent(resourceSlug)}/`
         }
      },
   },
   resourceItem: {
      path: "/[resourceType]/[resourceSlug]",
      getPath: (params: { resourceType: ResourceType; resourceSlug: string }) =>
         `/${encodeURIComponent(formatTypeForUrl(params.resourceType))}/${encodeURIComponent(params.resourceSlug)}/`,
   },

   resourceTypePage: {
      path: "/resources/[resourceType]/",
      getPath: ({ resourceType, categorySlug }: { resourceType: ResourceType; categorySlug?: string }) => {
         const formattedType = formatTypeForUrl(resourceType)

         if (!categorySlug || categorySlug.toLowerCase() === "all") return `/resources/${formattedType}/`
         return `/resources/${formattedType}/${encodeURIComponent(categorySlug)}/`
      },
   },

   resourceCalculators: {
      path: "/resources/tools",
      getPath: () => "/resources/tools/",
   },
   resourceLaborCostCalculator: {
      path: "/resources/calculators/labor-cost-calculator",
      getPath: () => "/resources/calculators/labor-cost-calculator/",
   },
   resourcePosRevenueLeakageCalculator: {
      path: "/resources/calculators/pos-revenue-leakage-calculator",
      getPath: () => "/resources/calculators/pos-revenue-leakage-calculator/",
   },
   resourceTimeCalculator: {
      path: "/resources/calculators/time-calculator",
      getPath: () => "/resources/calculators/time-calculator/",
   },
   resourceForecastCalculator: {
      path: "/resources/calculators/forecast-calculator",
      getPath: () => "/resources/calculators/forecast-calculator/",
   },
   topics: {
      path: "/blog",
      getPath: () => "/blog/",
   },
   topic: {
      path: "/blog/[slug]",
      getPath: (params: { slug: string }) => `/blog/${encodeURIComponent(params.slug)}/`,
   },
   newsPage: {
      path: "/news",
      getPath: () => "/news/",
   },
   news: {
      path: "/news/[slug]",
      getPath: (params: { slug: string }) => `/news/${encodeURIComponent(params.slug)}/`,
   },
   unified: {
      path: "/unified",
      getPath: () => "/unified/",
   },
   unifiedLogin: {
      path: "/unified/login",
      getPath: () => "/unified/login/",
   },
   unifiedLoginV2: {
      path: "/v2/unified/login",
      getPath: () => "/v2/unified/login/",
   },
   // The delegated OIDC entry point. Under RAAUTH_ONLY, link here directly rather than to
   // /unified/login — that path only reaches here via middleware's RAAUTH_REDIRECTS plus a
   // trailing-slash canonicalisation, i.e. two extra round trips.
   unifiedAuth: {
      path: "/unified/auth",
      getPath: () => "/unified/auth/",
   },
   // Uid-free magic-link redemption. The emailed link lands here; confirming redeems the
   // token and starts a fresh authorization from THIS browser (see magic-proof.ts).
   unifiedMagicConfirm: {
      path: "/unified/magic-link/confirm",
      getPath: () => "/unified/magic-link/confirm/",
   },
   unifiedAuthError: {
      path: "/unified/error",
      getPath: () => "/unified/error/",
   },
   unifiedAuthConfirmRequest: {
      path: "/unified/confirm-request",
      getPath: ({ queryParams }: { queryParams?: string }) =>
         "/unified/confirm-request/" + (queryParams ? `?${queryParams}` : ""),
   },
   unifiedAuthHelp: {
      path: "/unified/auth-help",
      getPath: () => "/unified/auth-help",
   },

   unifiedSignup: {
      path: "/unified/signup",
      getPath: () => "/unified/signup",
   },
   unifiedSignupV2: {
      path: "/v2/unified/signup",
      getPath: () => "/v2/unified/signup",
   },
   unifiedSignupEmail: {
      path: "/unified/signup",
      getPath: (email?: string) => "/unified/signup/?email=" + encodeURIComponent(email ?? ""),
   },
   unifiedSignupSuccess: {
      path: "/unified/signup/success",
      getPath: () => "/unified/signup/success/",
   },
   unifiedSignipSucessMemberBenefits: {
      path: "/unified/signup/success/#membership-benefits",
      getPath: () => "/unified/signup/success/#membership-benefits",
   },
   unifiedPasswordReset: {
      path: "/unified/password-reset",
      getPath: () => "/unified/password-reset",
   },
   unifiedPasswordResetV2: {
      path: "/v2/unified/password-reset",
      getPath: () => "/v2/unified/password-reset",
   },
   unifiedPasswordResetEmail: {
      path: "/unified/password-reset/email",
      getPath: () => "/unified/password-reset/email/",
   },
   unifiedPasswordResetEmailV2: {
      path: "/v2/unified/password-reset/email",
      getPath: () => "/v2/unified/password-reset/email/",
   },
   welcome: {
      path: "/welcome/",
      getPath: () => "/welcome/",
   },
   sitemap: {
      path: "/sitemap.xml",
      getPath: () => "/sitemap.xml/",
   },
   benefits: {
      path: "/benefits",
      getPath: () => "/benefits",
   },
   profileEdit: {
      path: "/profile/edit",
      getPath: () => "/profile/edit/",
   },
   profileBenefits: {
      path: "/profile/benefits",
      getPath: () => "/profile/benefits/",
   },
   adminBulkTest: {
      path: "/admin/bulk-test",
      getPath: () => "/admin/bulk-test",
   },
} as const

// API routes
const apiRoutes = {
   analytics: {
      path: "/api/analytics",
      getPath: () => "/api/analytics/",
   },
   analyticsItem: {
      path: "/api/analytics/[itemId]",
      getPath: (params: { itemId: string }) => `/api/analytics/${encodeURIComponent(params.itemId)}/`,
   },
   analyticsMautic: {
      path: "/api/analytics/mautic",
      getPath: () => "/api/analytics/mautic/",
   },
   analyticsReset: {
      path: "/api/analytics/reset",
      getPath: () => "/api/analytics/reset/",
   },
   analyticsTop: {
      path: "/api/analytics/top",
      getPath: () => "/api/analytics/top/",
   },
   articles: {
      path: "/api/articles",
      getPath: () => "/api/articles/",
   },
   article: {
      path: "/api/articles/[slug]",
      getPath: (params: { slug: string }) => `/api/articles/${encodeURIComponent(params.slug)}/`,
   },
   articlesSyncLocal: {
      path: "/api/articles/sync-local",
      getPath: () => "/api/articles/sync-local/",
   },
   authLogin: {
      path: "/api/auth/login",
      getPath: () => "/api/auth/login/",
   },
   authLogout: {
      path: "/api/auth/logout",
      getPath: () => "/api/auth/logout/",
   },
   authResetPassword: {
      path: "/api/auth/reset-password",
      getPath: () => "/api/auth/reset-password/",
   },
   authSendPasswordResetEmail: {
      path: "/api/auth/send-password-reset-email",
      getPath: () => "/api/auth/send-password-reset-email/",
   },
   authSignup: {
      path: "/api/auth/signup",
      getPath: () => "/api/auth/signup/",
   },
   authors: {
      path: "/api/authors/[id]",
      getPath: (params: { id: string }) => `/api/authors/${encodeURIComponent(params.id)}/`,
   },
   categories: {
      path: "/api/categories",
      getPath: () => "/api/categories/",
   },
   emailArticles: {
      path: "/api/email/articles",
      getPath: () => "/api/email/articles/",
   },
   mautic: {
      path: "/api/mautic",
      getPath: () => "/api/mautic/",
   },
   mautic2: {
      path: "/api/mautic/2",
      getPath: () => "/api/mautic/2/",
   },
   v2MauticFormSubmit: {
      path: "/api/v2/mautic/[formId]",
      getPath: (params: { formId: string }) => `/api/v2/mautic/${encodeURIComponent(params.formId)}/`,
   },
   resources: {
      path: "/api/resources",
      getPath: () => "/api/resources/",
   },
   resourcesGrouped: {
      path: "/api/resources/grouped",
      getPath: () => "/api/resources/grouped/",
   },
   resourcesLibrary: {
      path: "/api/resources/library",
      getPath: () => "/api/resources/library/",
   },
   search: {
      path: "/api/search",
      getPath: (params: {
         query?: string
         articles?: boolean
         videos?: boolean
         library?: boolean
         templates?: boolean
      }) =>
         `/api/search/?${params.query ? `q=${encodeURIComponent(params.query)}` : ""}${params.articles ? `&articles=${params.articles ? 1 : 0}` : ""}${params.videos ? `&videos=${params.videos ? 1 : 0}` : ""}${params.library ? `&library=${params.library ? 1 : 0}` : ""}${params.templates ? `&templates=${params.templates ? 1 : 0}` : ""}`,
   },
   seedCategories: {
      path: "/api/seed/categories",
      getPath: () => "/api/seed/categories/",
   },
   seedResources: {
      path: "/api/seed/resources",
      getPath: () => "/api/seed/resources/",
   },
   staffCreateBulk: {
      path: "/api/staff/create/bulk",
      getPath: () => "/api/staff/create/bulk/",
   },
   staffCreateBulkCategories: {
      path: "/api/staff/create/bulk/categories",
      getPath: () => "/api/staff/create/bulk/categories/",
   },
   unifiedLoginApi: {
      path: "/api/unified/login",
      getPath: () => "/api/unified/login/",
   },
   unifiedLoginExternalApi: {
      path: "/api/unified/login/external",
      getPath: () => "/api/unified/login/external/",
   },
   unifiedLogoutApi: {
      path: "/api/unified/logout",
      getPath: () => "/api/unified/logout/",
   },
   unifiedSignupApi: {
      path: "/api/unified/signup",
      getPath: () => "/api/unified/signup/",
   },
   unifiedAccountSignupApi: {
      path: "/api/unified/accountSignup",
      getPath: () => "/api/unified/accountSignup/",
   },
   unifiedAccountSignupApiV2: {
      path: "/api/v2/unified/accountSignup",
      getPath: () => "/api/v2/unified/accountSignup/",
   },
   unifiedAccountCompleteApi: {
      path: "/api/unified/accountComplete",
      getPath: () => "/api/unified/accountComplete/",
   },
   unifiedExternalAccountSignupApi: {
      path: "/api/unified/externalAccountSignup",
      getPath: () => "/api/unified/externalAccountSignup/",
   },
   upload: {
      path: "/api/upload",
      getPath: () => "/api/upload/",
   },
   users: {
      path: "/api/users",
      getPath: () => "/api/users/",
   },
   user: {
      path: "/api/users/[userId]",
      getPath: (params: { userId: string }) => `/api/users/${encodeURIComponent(params.userId)}/`,
   },
   userEmail: {
      path: "/api/users/email/[email]",
      getPath: (params: { email: string }) => `/api/users/email/${encodeURIComponent(params.email)}/`,
   },
   videos: {
      path: "/api/videos",
      getPath: () => "/api/videos/",
   },
   video: {
      path: "/api/videos/[videoId]",
      getPath: (params: { videoId: string }) => `/api/videos/${encodeURIComponent(params.videoId)}/`,
   },
   videosLatest: {
      path: "/api/videos/latest",
      getPath: () => "/api/videos/latest/",
   },
   videosSync: {
      path: "/api/videos/sync",
      getPath: () => "/api/videos/sync/",
   },
} as const

const externalRoutes = {
   finalCutMultimedia: {
      path: "https://finalcutmultimedia.com",
      getPath: () => "https://finalcutmultimedia.com",
   },
   foodHandlersCourse: {
      path: "https://training.restaurantassociation.com/home/courses?category=safe-food-handling",
      getPath: () =>
         `${process.env.NEXT_PUBLIC_TRAINING_BASE_URL ?? "https://training.restaurantassociation.com"}/home/courses?category=safe-food-handling`,
   },
   PostAJob: {
      path: "https://jobs.restaurantassociation.com/products/?permission=post_job",
      getPath: ({ jobs_session }: { jobs_session?: string }) => {
         const base = process.env.NEXT_PUBLIC_JOBS_BASE_URL ?? "https://jobs.restaurantassociation.com"
         return `${base}/products?${jobs_session ? `session=${jobs_session}` : ""}`
      },
   },
   jobsByState: {
      path: "https://jobs.restaurantassociation.com/jobs/",
      getPath: () => `${process.env.NEXT_PUBLIC_JOBS_BASE_URL ?? "https://jobs.restaurantassociation.com"}/jobs/`,
   },
   training: {
      path: "https://training.restaurantassociation.com/",
      getPath: () => `${process.env.NEXT_PUBLIC_TRAINING_BASE_URL ?? "https://training.restaurantassociation.com"}/`,
   },
} as const

// Combined routes object
export const Routes = {
   client: clientRoutes,
   api: apiRoutes,
   external: externalRoutes,
} as const

// Utility types for type safety
export type ClientRouteKeys = keyof typeof clientRoutes
export type ApiRouteKeys = keyof typeof apiRoutes
export type RoutePath = (typeof clientRoutes)[ClientRouteKeys]["path"] | (typeof apiRoutes)[ApiRouteKeys]["path"]
